import { goto } from '$app/navigation';
import { page } from '$app/state';

// Non-printable separator used to join multiple filter values in the URL
export const US = String.fromCharCode(31);

export type FilterValue = { value: string; count: number; is_main: boolean };
export type Filter =
	| {
			type: 'terms';
			data: TermsFilter;
	  }
	| {
			type: 'range';
			data: RangeFilter;
	  };
export type FilterType = Filter['type'];
export type TermsFilter = {
	name: string;
	values: FilterValue[];
};
export type RangeFilter = {
	name: string;
	min: number;
	max: number;
};

export enum CategoriesSortOptions {
	Relevance = 'relevance',
	PriceAsc = 'price_asc',
	PriceDesc = 'price_desc',
	Discount = 'discount',
}

export class FilterProvider {
	checkedFilters: Record<string, string[]> = $state({});

	update(filters: Filter[]) {
		const currParams = page.url.searchParams;
		const newCheckedFilters: Record<string, string[]> = {};
		filters.forEach((filter) => {
			if (currParams.has(filter.data.name)) {
				newCheckedFilters[filter.data.name] = (currParams.get(filter.data.name) ?? '').split(US);
			}
		});
		this.checkedFilters = newCheckedFilters;
	}

	updateQueryParams(name: string | undefined = undefined) {
		const params = new URLSearchParams(page.url.searchParams);

		for (const filter of name === undefined ? Object.keys(this.checkedFilters) : [name]) {
			const paramValue = this.toParamValues(filter);

			if (paramValue !== '') {
				params.set(filter, this.toParamValues(filter));
			} else {
				params.delete(filter);
			}
		}

		goto(`?${params}`);
	}

	toParamValues(name: string) {
		return this.checkedFilters[name].filter((values) => values.length).join(US);
	}

	reset() {
		for (const name in this.checkedFilters) {
			this.checkedFilters[name] = [];
		}

		this.updateQueryParams();
	}

	remove(name: string, value: string | undefined = undefined) {
		if (value === undefined) {
			this.checkedFilters[name] = [];
			this.updateQueryParams(name);
			return;
		}

		const idx = this.checkedFilters[name].indexOf(value);
		if (idx !== -1) this.checkedFilters[name].splice(idx, 1);
		this.updateQueryParams(name);
	}

	toggle(name: string, value: string) {
		if (!(name in this.checkedFilters)) {
			// Directly setting the value to prevent doing two update of value
			// which could trigger effect 2 times
			this.checkedFilters[name] = [value];
			this.updateQueryParams(name);
			return;
		}

		if (this.checkedFilters[name].includes(value)) {
			this.remove(name, value);
			return;
		}

		this.checkedFilters[name].push(value);
		this.updateQueryParams(name);
	}

	toggleBoolean(name: string) {
		if (!(name in this.checkedFilters)) {
			this.checkedFilters[name] = [];
			this.updateQueryParams(name);
		}

		if (this.checkedFilters[name].includes('true')) {
			this.remove(name, 'true');
			return;
		}

		this.checkedFilters[name].push('true');
		this.updateQueryParams(name);
	}

	setValues(name: string, values: string[]) {
		this.checkedFilters[name] = values;
		this.updateQueryParams(name);
	}

	isChecked(name: string, value: string) {
		return this.checkedFilters?.[name]?.includes(value);
	}

	getSelectedCount(name: string | undefined = undefined): number {
		return Object.entries(this.checkedFilters)
			.filter(([n]) => name === undefined || n === name)
			.reduce((count, [, v]) => count + v.length, 0);
	}

	displayValue(value: string) {
		return value.includes(':') ? value.split(':').slice(1).join(':') : value;
	}

	orderedFilters(filter: TermsFilter, searchQuery: string, sortByMain: boolean) {
		const filters = sortByMain
			? filter.values.toSorted((a, b) => {
					// Priority: main only
					if (a.is_main && !b.is_main) return -1;
					if (!a.is_main && b.is_main) return 1;
					return 0;
				})
			: filter.values;

		return filters
			.filter((filterValue) =>
				this.displayValue(filterValue.value).toLowerCase().includes(searchQuery.toLowerCase()),
			)
			.sort((a, b) => {
				// Priority: checked > starts with > alphabetical

				const aChecked = this.isChecked(filter.name, a.value) ? 1 : 0;
				const bChecked = this.isChecked(filter.name, b.value) ? 1 : 0;
				if (bChecked !== aChecked) return bChecked - aChecked;

				const aStartsWith = this.displayValue(a.value)
					.toLowerCase()
					.startsWith(searchQuery.toLowerCase())
					? 1
					: 0;
				const bStartsWith = this.displayValue(b.value)
					.toLowerCase()
					.startsWith(searchQuery.toLowerCase())
					? 1
					: 0;
				if (bStartsWith !== aStartsWith) return bStartsWith - aStartsWith;
				return this.displayValue(a.value).localeCompare(this.displayValue(b.value));
			});
	}
}

export default FilterProvider;
